import axios from 'axios'
import type { AxiosResponse } from 'axios'

import { ApiError, toApiError } from './errors'
import type { ApiClient, ApiClientOptions, ApiRequestOptions, HttpMethod } from './types'

const requestWithMethod =
  (request: ApiClient['request'], method: HttpMethod) =>
  <TResponse, TBody = unknown, TParams = unknown>(
    path: string,
    options?: Omit<ApiRequestOptions<TResponse, TBody, TParams>, 'method' | 'path'>
  ) => {
    return request<TResponse, TBody, TParams>({ ...options, method, path })
  }

export const createApiClient = ({
  baseURL,
  timeoutMs = 10000,
  withCredentials = true,
  headers,
  configure,
}: ApiClientOptions): ApiClient => {
  const instance = axios.create({
    baseURL,
    timeout: timeoutMs,
    withCredentials,
    headers: {
      'Content-type': 'application/json',
      ...headers,
    },
  })

  configure?.(instance)

  const request: ApiClient['request'] = async <TResponse, TBody = unknown, TParams = unknown>({
    method,
    path,
    body,
    params,
    schema,
    ...config
  }: ApiRequestOptions<TResponse, TBody, TParams>) => {
    try {
      const response = (await instance.request({
        ...config,
        method,
        url: path,
        data: body,
        params,
      })) as AxiosResponse<TResponse>

      if (!schema) {
        return response.data
      }

      const parsedResult = schema.safeParse(response.data)
      if (!parsedResult.success) {
        throw new ApiError('Response validation failed', {
          details: parsedResult.error,
        })
      }

      return parsedResult.data
    } catch (error) {
      throw toApiError(error)
    }
  }

  return {
    raw: instance,
    request,
    get: requestWithMethod(request, 'GET'),
    post: requestWithMethod(request, 'POST'),
    put: requestWithMethod(request, 'PUT'),
    patch: requestWithMethod(request, 'PATCH'),
    delete: requestWithMethod(request, 'DELETE'),
  }
}
